import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Check } from "lucide-react";
import { buildPathFor } from "@/hooks/useLangPath";

const LANGS = [
  {
    code: "it",
    label: "Italiano",
    short: "IT",
    flag: (
      <svg viewBox="0 0 3 2" className="w-full h-full" preserveAspectRatio="none" aria-hidden="true">
        <rect width="1" height="2" x="0" fill="#009246" />
        <rect width="1" height="2" x="1" fill="#fff" />
        <rect width="1" height="2" x="2" fill="#ce2b37" />
      </svg>
    ),
  },
  {
    code: "en",
    label: "English",
    short: "EN",
    flag: (
      <svg viewBox="0 0 60 30" className="w-full h-full" preserveAspectRatio="none" aria-hidden="true">
        <clipPath id="cc-gb-t"><path d="M0,0 v30 h60 v-30 z" /></clipPath>
        <clipPath id="cc-gb-s"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" /></clipPath>
        <g clipPath="url(#cc-gb-t)">
          <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
          <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
          <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#cc-gb-s)" stroke="#C8102E" strokeWidth="4" />
          <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
          <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
        </g>
      </svg>
    ),
  },
  {
    code: "ja",
    label: "日本語",
    short: "JP",
    flag: (
      <svg viewBox="0 0 3 2" className="w-full h-full" preserveAspectRatio="none" aria-hidden="true">
        <rect width="3" height="2" fill="#fff" />
        <circle cx="1.5" cy="1" r="0.6" fill="#bc002d" />
      </svg>
    ),
  },
];

const LanguageSwitcher = ({ compact = false }) => {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Determine current lang from URL (source of truth)
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const urlLang = ["en", "ja"].includes(pathSegments[0]) ? pathSegments[0] : "it";
  const current = LANGS.find((l) => l.code === urlLang) || LANGS[0];

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const change = (code) => {
    // Compute base path without any existing lang prefix
    const segs = location.pathname.split("/").filter(Boolean);
    const remaining = ["en", "ja"].includes(segs[0]) ? segs.slice(1) : segs;
    const basePath = remaining.length ? `/${remaining.join("/")}` : "";
    const target = buildPathFor(code, basePath) + (location.hash || "");
    i18n.changeLanguage(code);
    navigate(target);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full hover:bg-[#ecdcbd] transition focus-ring"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("nav.changeLang")}
        data-testid="lang-switcher-btn"
      >
        <span
          className="inline-block w-6 h-4 rounded-sm overflow-hidden ring-1 ring-black/10"
          style={{ background: "#fff" }}
        >
          {current.flag}
        </span>
        {!compact && (
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#3b2417" }}>
            {current.short}
          </span>
        )}
        <ChevronDown
          size={13}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
          style={{ color: "#3b2417" }}
        />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 min-w-[180px] rounded-xl overflow-hidden shadow-xl z-50"
          style={{ background: "#fbf5e9", border: "1px solid rgba(91,54,33,0.15)", boxShadow: "0 20px 40px -15px rgba(42,22,12,0.25)" }}
          role="listbox"
          data-testid="lang-switcher-dropdown"
        >
          <div className="h-1 w-full" style={{ background: "#c5371f" }} />
          <ul>
            {LANGS.map((l) => {
              const active = l.code === current.code;
              return (
                <li key={l.code}>
                  <button
                    onClick={() => change(l.code)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm transition hover:bg-[#ecdcbd]"
                    style={{ color: "#3b2417" }}
                    role="option"
                    aria-selected={active}
                    data-testid={`lang-option-${l.code}`}
                  >
                    <span className="inline-block w-6 h-4 rounded-sm overflow-hidden ring-1 ring-black/10" style={{ background: "#fff" }}>
                      {l.flag}
                    </span>
                    <span className="flex-1 text-left font-medium">{l.label}</span>
                    {active && <Check size={14} style={{ color: "#c5371f" }} />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
